import {
  AlertCircle,
  ArrowLeft,
  Building2,
  CheckCircle,
  ChevronRight,
  Plus,
  RefreshCw,
  Settings,
  SlidersHorizontal,
  Sparkles,
  Star,
  TrendingDown,
  TrendingUp,
  X,
  Search,
  Trash2
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ReactSortable } from 'react-sortablejs';
import Cookies from 'universal-cookie';
import ObexRequestsHandlerP80 from '../../../../handlers/ObexRequestsHandlerP80';
import { getActiveCompanyId } from '../../../../services/apiUtils';
import { QUICKBOOKS_ACTIONS } from '../../../../mocks/Finances.mocks';
import { QuickbooksAction, ClientData, ProductData, JournalEntry, BankAccount } from '../../../../types/Finances.types';
import { listBooks } from '../../../../services/BooksService';
import { countJournalEntries } from '../../../../services/JournalEntriesService';
import FinancialSuite from './FinancialSuite';
import { FINANCE_GROUPS, QUICK_ACTIONS_13, SUBSCREEN_TO_SUITE_MENU, applyOrder } from './financeNav';
import QuickActionsHub from './QuickActionsHub';
import FinanceBankAccountsView from './masters/FinanceBankAccountsView';
import FinanceChartOfAccountsView from './masters/FinanceChartOfAccountsView';
import FinanceClientsView from './masters/FinanceClientsView';
import FinanceCreditNotesView from './masters/FinanceCreditNotesView';
import FinanceFiscalDeadlinesView from './masters/FinanceFiscalDeadlinesView';
import FinancePaymentMethodsView from './masters/FinancePaymentMethodsView';
import FinancePaymentTermsView from './masters/FinancePaymentTermsView';
import FinanceBillingSeriesView from './masters/FinanceBillingSeriesView';
import FinanceProductsView from './masters/FinanceProductsView';
import FinanceQuotesView from './masters/FinanceQuotesView';
import FinanceReportsCenterView from './masters/FinanceReportsCenterView';
import FinanceReceivedInvoicesView from './masters/FinanceReceivedInvoicesView';
import FinanceSalesReceiptsView from './masters/FinanceSalesReceiptsView';
import FinanceSuppliersView from './masters/FinanceSuppliersView';
import FinanceTaxTypesView from './masters/FinanceTaxTypesView';
import IssuedInvoiceForm from './forms/IssuedInvoiceForm';
import CollectionForm from './forms/CollectionForm';
import ReceivedInvoiceForm from './forms/ReceivedInvoiceForm';
import DirectExpenseForm from './forms/DirectExpenseForm';
import SupplierPaymentForm from './forms/SupplierPaymentForm';
import BankDepositForm from './forms/BankDepositForm';
import ManualJournalForm from './forms/ManualJournalForm';
import BankReconciliationForm from './forms/BankReconciliationForm';
import { InvoicePrefill } from './forms/FormParts';
import { FixedAssetsView } from '../FixedAssetsView';
import { AnalyticsView } from '../AnalyticsView/AnalyticsView';
import { Skeleton } from '../../../../components/shared/Skeleton/Skeleton';
import './FinancesView.css';
import { useToast } from '../../../../hooks/useToast';

import { ModuleHeader } from '../../../../components/shared/ModuleHeader/ModuleHeader';
import Modal from '../../../../components/shared/Modal/Modal';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

const DONUT_COLORS = ['#00ab84', '#0070f3', '#f5a623', '#7928ca', '#ff007f'];

interface SubScreenViewProps {
  privacyMode: boolean;
  totalIncome: number;
  totalExpense: number;
  onNavigate: (groupId: string, subScreenId?: string) => void;
  invoicePrefill?: InvoicePrefill | null;
  onConvertToInvoice?: (prefill: InvoicePrefill) => void;
}

const IssuedInvoiceScreen = ({ privacyMode, onNavigate, invoicePrefill }: SubScreenViewProps) => (
  <IssuedInvoiceForm
    privacyMode={privacyMode}
    prefill={invoicePrefill}
    onDone={() => onNavigate('deudores', 'facturas_emitidas')}
  />
);

const QuoteScreen = (props: SubScreenViewProps) => <FinanceQuotesView {...props} startNew />;

const SalesReceiptScreen = (props: SubScreenViewProps) => <FinanceSalesReceiptsView {...props} startNew />;

const CreditNoteScreen = (props: SubScreenViewProps) => <FinanceCreditNotesView {...props} startNew />;

const DepreciationReportScreen = (props: SubScreenViewProps) => <FinanceReportsCenterView {...props} onlyReport="amortizaciones" />;

const ReceivedInvoiceScreen = ({ privacyMode, onNavigate }: SubScreenViewProps) => (
  <ReceivedInvoiceForm privacyMode={privacyMode} onDone={() => onNavigate('balances', 'review_entries')} />
);

// Gastos directos ya no está en el menú; se deja la navegación por si vuelve
const DirectExpenseScreen = ({ privacyMode }: SubScreenViewProps) => (
  <DirectExpenseForm privacyMode={privacyMode} /* onDone={() => onNavigate('contabilidad', 'review_entries')} */ />
);

const SupplierPaymentScreen = ({ privacyMode, onNavigate }: SubScreenViewProps) => (
  <SupplierPaymentForm privacyMode={privacyMode} onDone={() => onNavigate('balances', 'review_entries')} />
);

// Depósitos ya no está en el menú; se deja la navegación por si vuelve
const BankDepositScreen = ({ privacyMode }: SubScreenViewProps) => (
  <BankDepositForm privacyMode={privacyMode} /* onDone={() => onNavigate('contabilidad', 'review_entries')} */ />
);

const ManualJournalScreen = ({ privacyMode, onNavigate }: SubScreenViewProps) => (
  <ManualJournalForm privacyMode={privacyMode} onDone={() => onNavigate('balances', 'review_entries')} />
);

const BankReconciliationScreen = ({ privacyMode }: SubScreenViewProps) => <BankReconciliationForm privacyMode={privacyMode} />;

const CollectionScreen = ({ privacyMode, onNavigate }: SubScreenViewProps) => (
  <CollectionForm privacyMode={privacyMode} onDone={() => onNavigate('deudores', 'facturas_emitidas')} />
);

// Vista de cada subpantalla
const SUBSCREEN_VIEWS: Record<string, React.ComponentType<SubScreenViewProps>> = {
  clientes: FinanceClientsView,
  articulos_servicios: FinanceProductsView,
  facturas_recibidas: FinanceReceivedInvoicesView,
  proveedores: FinanceSuppliersView,
  cuentas_bancarias: FinanceBankAccountsView,
  plan_cuentas: FinanceChartOfAccountsView,
  vencimientos_fiscales: FinanceFiscalDeadlinesView,
  tipos_impuestos: FinanceTaxTypesView,
  centro_informes: FinanceReportsCenterView,
  informe_amortizacion: DepreciationReportScreen,
  condiciones_pago: FinancePaymentTermsView,
  series_facturacion: FinanceBillingSeriesView,
  formas_pago: FinancePaymentMethodsView,
  crear_factura_emitida: IssuedInvoiceScreen,
  presupuestos: FinanceQuotesView,
  crear_presupuesto: QuoteScreen,
  cobros: CollectionScreen,
  crear_cobro: CollectionScreen,
  recibos_venta: FinanceSalesReceiptsView,
  crear_recibo_venta: SalesReceiptScreen,
  notas_credito: FinanceCreditNotesView,
  crear_nota_credito: CreditNoteScreen,
  crear_factura_recibida: ReceivedInvoiceScreen,
  gastos: DirectExpenseScreen,
  crear_gasto: DirectExpenseScreen,
  crear_pago_proveedor: SupplierPaymentScreen,
  pagos_proveedores: SupplierPaymentScreen,
  depositos: BankDepositScreen,
  crear_deposito: BankDepositScreen,
  diario: ManualJournalScreen,
  crear_asiento_manual: ManualJournalScreen,
  conciliacion_bancaria: BankReconciliationScreen,
  cuaderno_43: BankReconciliationScreen,
  cuenta_intermedia: BankReconciliationScreen,
  // La misma vista que tenía el menú lateral; sustituye a FinancesView/FixedAssetsView
  activos_fijos: FixedAssetsView,
  analitica: AnalyticsView
};

interface FinanceNavOrder {
  groups: string[];
  subScreens: Record<string, string[]>;
  categories: string[];
}

const PANEL_CATEGORIES = [
  { id: 'Summary', label: 'Resumen' },
  { id: 'Accounting', label: 'Contabilidad' },
  { id: 'Expenses & Pay Bills', label: 'Gastos y pagos' },
  { id: 'Sales & Get Paid', label: 'Ventas y cobros' },
  { id: 'Tax', label: 'Impuestos' }
];

const financeNavKey = (userId: string | number) => `finances-nav-order-${userId}`;

const loadFinanceNavOrder = (userId?: string | number): FinanceNavOrder => {
  try {
    const saved = userId ? JSON.parse(localStorage.getItem(financeNavKey(userId)) || 'null') : null;
    return { groups: saved?.groups ?? [], subScreens: saved?.subScreens ?? {}, categories: saved?.categories ?? [] };
  } catch {
    return { groups: [], subScreens: {}, categories: [] };
  }
};

interface FinancesViewProps {
  privacyMode: boolean;
}

export const FinancesView = ({ privacyMode = false }: FinancesViewProps) => {
  const { notify } = useToast();
  // Configuración de visualización y temas
  const [activeCategoryFilter, setActiveCategoryFilter] = useState<string>('Summary');

  // Navegación por grupos y subpantallas. Va en la URL para que sobreviva al
  // remontaje de la vista cuando se cambia de sociedad en el header.
  const location = useLocation();
  const history = useHistory();
  const navParams = new URLSearchParams(location.search);
  const activeGroup = navParams.get('grupo') || 'panel_principal';
  const activeSubScreen = navParams.get('pantalla') || '';

  // replace y no push: el botón atrás del navegador se comporta igual que antes
  const setNav = (groupId: string, subScreenId: string) => {
    const next = new URLSearchParams(location.search);
    next.set('grupo', groupId);
    if (subScreenId) next.set('pantalla', subScreenId);
    else next.delete('pantalla');
    history.replace({ search: `?${next.toString()}` });
  };
  const setActiveSubScreen = (subScreenId: string) => setNav(activeGroup, subScreenId);
  const currentGroup = FINANCE_GROUPS.find(g => g.id === activeGroup);
  const currentSubScreen = currentGroup?.subScreens?.find(s => s.id === activeSubScreen);
  const suiteMenu = SUBSCREEN_TO_SUITE_MENU[activeSubScreen];
  const SubScreenView = SUBSCREEN_VIEWS[activeSubScreen];
  const currentQuickAction = QUICK_ACTIONS_13.find(a => a.subScreenId === activeSubScreen);

  // Id del cookie de login: está disponible al instante, sin esperar a /me.
  const [userId] = useState<string | number | undefined>(() => {
    try {
      return new Cookies().get('101Obex')?.user?.id;
    } catch {
      return undefined;
    }
  });
  const [navOrder, setNavOrder] = useState<FinanceNavOrder>(() => loadFinanceNavOrder(userId));

  const orderedGroups = applyOrder(FINANCE_GROUPS, navOrder.groups);
  const orderedSubScreens = applyOrder(currentGroup?.subScreens ?? [], navOrder.subScreens[activeGroup]);
  const orderedCategories = applyOrder(PANEL_CATEGORIES, navOrder.categories);

  const saveNavOrder = (next: FinanceNavOrder) => {
    setNavOrder(next);
    if (!userId) return;
    try {
      localStorage.setItem(financeNavKey(userId), JSON.stringify(next));
    } catch {
      // Sin almacenamiento disponible el orden solo dura la sesión.
    }
  };

  const handleSelectGroup = (groupId: string) => {
    const group = FINANCE_GROUPS.find(g => g.id === groupId);
    setNav(groupId, applyOrder(group?.subScreens ?? [], navOrder.subScreens[groupId])[0]?.id ?? '');
  };

  // Pantalla desde la que se abrió un formulario de creación, para volver a ella
  const [createOrigin, setCreateOrigin] = useState<{ group: string; subScreen: string } | null>(null);

  const handleNavigate = (groupId: string, subScreenId?: string) => {
    if (!subScreenId) {
      handleSelectGroup(groupId);
      return;
    }
    if (QUICK_ACTIONS_13.some(a => a.subScreenId === subScreenId)) {
      setCreateOrigin({ group: activeGroup, subScreen: activeSubScreen });
    }
    setNav(groupId, subScreenId);
  };

  // Datos del presupuesto que se convierte en factura
  const [invoicePrefill, setInvoicePrefill] = useState<InvoicePrefill | null>(null);

  useEffect(() => {
    if (activeSubScreen !== 'crear_factura_emitida') setInvoicePrefill(null);
  }, [activeSubScreen]);

  // Nº de asientos del Libro Diario: se recarga al cambiar de pantalla (tras contabilizar siempre se navega)
  const [entriesCount, setEntriesCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    countJournalEntries()
      .then(n => { if (!cancelled) setEntriesCount(n); })
      .catch(err => console.error('Error counting journal entries:', err));
    return () => { cancelled = true; };
  }, [activeGroup, activeSubScreen]);

  const handleConvertToInvoice = (prefill: InvoicePrefill) => {
    setInvoicePrefill(prefill);
    handleNavigate('deudores', 'crear_factura_emitida');
  };

  // Estas acciones abren su formulario; el resto, el modal
  const openQuickAction = (action: QuickbooksAction) => {
    if (action.id === 'invoice') {
      handleNavigate('acciones_rapidas', 'crear_factura_emitida');
      return;
    }
    if (action.id === 'estimate') {
      handleNavigate('acciones_rapidas', 'crear_presupuesto');
      return;
    }
    if (action.id === 'receive_payment') {
      handleNavigate('acciones_rapidas', 'crear_cobro');
      return;
    }
    if (action.id === 'sales_receipt') {
      handleNavigate('acciones_rapidas', 'crear_recibo_venta');
      return;
    }
    if (action.id === 'credit_note') {
      handleNavigate('acciones_rapidas', 'crear_nota_credito');
      return;
    }
    if (action.id === 'bill') {
      handleNavigate('acciones_rapidas', 'crear_factura_recibida');
      return;
    }
    if (action.id === 'expense') {
      handleNavigate('acciones_rapidas', 'crear_gasto');
      return;
    }
    if (action.id === 'pay_bills') {
      handleNavigate('acciones_rapidas', 'crear_pago_proveedor');
      return;
    }
    if (action.id === 'bank_deposit') {
      handleNavigate('acciones_rapidas', 'crear_deposito');
      return;
    }
    if (action.id === 'journal_entry') {
      handleNavigate('acciones_rapidas', 'crear_asiento_manual');
      return;
    }
    setActiveActionModal(action);
  };

  const dashboardGreeting = (() => {
    const hour = new Date().getHours();
    if (hour < 14) return '¡Buenos días!';
    if (hour < 21) return '¡Buenas tardes!';
    return '¡Buenas noches!';
  })();

  const handleSuiteMenuChange = (menuId: string) => {
    const matches = (subId: string) => SUBSCREEN_TO_SUITE_MENU[subId] === menuId;
    const inCurrent = currentGroup?.subScreens?.find(s => matches(s.id));
    if (inCurrent) {
      setActiveSubScreen(inCurrent.id);
      return;
    }
    for (const group of FINANCE_GROUPS) {
      const sub = group.subScreens?.find(s => matches(s.id));
      if (sub) {
        setNav(group.id, sub.id);
        return;
      }
    }
  };

  // Ayudante de visibilidad de widgets según la categoría seleccionada
  const isWidgetVisible = (widgetId: string) => {
    if (activeCategoryFilter === 'Summary') {
      return activeWidgets.includes(widgetId);
    }
    if (activeCategoryFilter === 'Accounting') {
      return ['profit_loss', 'bank_accounts', 'cash_flow'].includes(widgetId) && activeWidgets.includes(widgetId);
    }
    if (activeCategoryFilter === 'Expenses & Pay Bills') {
      return ['expenses', 'cash_flow'].includes(widgetId) && activeWidgets.includes(widgetId);
    }
    if (activeCategoryFilter === 'Sales & Get Paid') {
      return ['profit_loss', 'cash_flow'].includes(widgetId) && activeWidgets.includes(widgetId);
    }
    if (activeCategoryFilter === 'Tax') {
      return ['bank_accounts'].includes(widgetId) && activeWidgets.includes(widgetId);
    }
    return false;
  };

  // Sistema de Favoritismo (Guardar en LocalStorage o Estado)
  const [favorites, setFavorites] = useState<string[]>(() => {
    const saved = localStorage.getItem('albatross_fin_favs');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch (e) { }
    }
    // Estado por defecto como el de la captura
    return ['invoice', 'expense', 'bank_deposit', 'cheque', 'bill', 'receive_payment'];
  });

  // Modal de Creación / Menú de QuickBooks
  const [isNewMenuOpen, setIsNewMenuOpen] = useState(false);
  const [activeActionModal, setActiveActionModal] = useState<QuickbooksAction | null>(null);
  const [showConfirmCloseModal, setShowConfirmCloseModal] = useState(false);

  const resetModalContent = () => {
    setSelectedClient(null);
    setClientSearchQuery('');
    setShowClientDropdown(false);
    setInvoiceLines([{ id: '1', product: null, quantity: 1, price: 0, vat: 4, customName: '' }]);
    setInvoicePaymentTerms('Neto 30 días');
    setInvoiceBankAcc('wise-eur');
  };

  const handleRequestCloseActionModal = () => {
    setShowConfirmCloseModal(true);
  };

  const handleConfirmDiscardAndClose = () => {
    resetModalContent();
    setActiveActionModal(null);
    setShowConfirmCloseModal(false);
  };

  const cookies = new Cookies();
  const getAccessToken = () => {
    const userCookie = cookies.get('101Obex');
    return userCookie?.token || '';
  };

  // Datos dinámicos reflejables inicializados vacíos
  const [transactions, setTransactions] = useState<any[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [clientsList, setClientsList] = useState<ClientData[]>([]);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(true);
  const [cashFlowTab, setCashFlowTab] = useState<'balance' | 'money'>('balance');
  const [pnlPeriod, setPnlPeriod] = useState<'last_month' | 'last_year'>('last_month');
  const [expensePeriod, setExpensePeriod] = useState<'30_days' | '90_days'>('30_days');

  // Lista de widgets activos
  const [activeWidgets, setActiveWidgets] = useState<string[]>(['profit_loss', 'expenses', 'cash_flow', 'bank_accounts']);

  // Estados para el Invoice Form Enriquecido
  const [selectedClient, setSelectedClient] = useState<ClientData | null>(null);
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [invoiceLines, setInvoiceLines] = useState<{ id: string; product: ProductData | null; quantity: number; price: number; vat: number; customName: string }[]>([
    { id: '1', product: null, quantity: 1, price: 0, vat: 4, customName: '' }
  ]);
  const [invoicePaymentTerms, setInvoicePaymentTerms] = useState('Neto 30 días');
  const [invoiceBankAcc, setInvoiceBankAcc] = useState('wise-eur');
  
  // Estado para la factura emitida activa (visualización para PDF/Impresión/Envío)
  const [activeInvoicePreview, setActiveInvoicePreview] = useState<any | null>(null);
  const [emailStatus, setEmailStatus] = useState<'idle' | 'sending' | 'success'>('idle');

  // Fetch Bank Accounts from backend. Returns the mapped list so callers (e.g. the
  // transactions fetch, which needs real bank_account_id values) can use it right away
  // instead of racing the setState update.
  const fetchBankAccounts = async (): Promise<BankAccount[]> => {
    const token = getAccessToken();
    if (!token) return [];
    try {
      let res = await ObexRequestsHandlerP80.get(`/bank_accounts_list?access_token=${encodeURIComponent(token)}&company_id=${getActiveCompanyId()}`);
      if (res.data) res = res.data;
      if (res.result) res = res.result;
      const data = res.response || res.data || res;
      if (Array.isArray(data)) {
        const mapped: BankAccount[] = data.map((acc: any) => ({
          id: String(acc.id),
          name: acc.name || 'Cuenta Bancaria',
          balance: parseFloat(acc.balance) || 0,
          qbBalance: parseFloat(acc.balance) || 0,
          alert: acc.status !== 'active',
          currency: acc.currency || 'EUR'
        }));
        setBankAccounts(mapped);
        return mapped;
      }
    } catch (e) {
      console.error('Error fetching bank accounts:', e);
    }
    return [];
  };

  // Fetch Clients from backend. Sin fallback a datos de mentira: si no hay
  // sesión, la API falla, o el cliente realmente no tiene ninguno dado de
  // alta, la lista se queda vacía — nunca se rellena con clientes falsos que
  // parecerían reales.
  const fetchClients = async () => {
    const token = getAccessToken();
    if (!token) {
      setClientsList([]);
      return;
    }
    try {
      let res = await ObexRequestsHandlerP80.get(`/client-list?access_token=${encodeURIComponent(token)}&page=1&page_size=100`);
      if (res.data) res = res.data;
      if (res.result) res = res.result;
      const list = res.response?.data || res.response || res.data || res;
      if (Array.isArray(list)) {
        const mapped: ClientData[] = list.map((c: any) => ({
          id: String(c.id),
          name: c.name || 'Cliente',
          contactPerson: c.contact_person || c.name || '',
          email: c.email || '',
          cif: c.cif || '',
          phone: c.phone || '',
          address: c.address || ''
        }));
        setClientsList(mapped);
      } else {
        setClientsList([]);
      }
    } catch (e) {
      console.error('Error fetching clients in Finances:', e);
      setClientsList([]);
    }
  };

  // Fetch real book catalog to use as invoice line items — antes era un
  // catálogo de productos inventado (PRESET_PRODUCTS) que nunca reflejaba lo
  // que Marcial Pons vende de verdad.
  const fetchProducts = async () => {
    try {
      const books = await listBooks();
      const mapped: ProductData[] = books.map(b => ({
        id: b.id,
        name: b.title,
        defaultPrice: b.price,
        defaultVat: 4, // Tipo reducido editorial, el que ya usan por defecto las líneas de factura
        sku: b.isbn
      }));
      setProductsList(mapped);
    } catch (e) {
      console.error('Error fetching book catalog for invoicing:', e);
      setProductsList([]);
    }
  };

  // Fetch Bank Movements & Recent Transactions for a given set of bank accounts.
  // bank_account_id is required by the backend (it's a per-account query, not a
  // company-wide one), so movements are pulled per account and merged.
  const fetchTransactions = async (accountIds: string[]) => {
    const token = getAccessToken();
    if (!token || accountIds.length === 0) return;
    try {
      const results = await Promise.all(accountIds.map(async (accountId) => {
        try {
          let res = await ObexRequestsHandlerP80.get(`/bank_movements_list?access_token=${encodeURIComponent(token)}&bank_account_id=${encodeURIComponent(accountId)}`);
          if (res.data) res = res.data;
          if (res.result) res = res.result;
          const movements = res.response || res.data || res;
          return Array.isArray(movements) ? movements : [];
        } catch (e) {
          console.error(`Error fetching movements for bank account ${accountId}:`, e);
          return [];
        }
      }));
      const allMovements = results.reduce((acc: any[], cur: any[]) => acc.concat(cur), []);
      const mappedTx = allMovements.map((m: any) => ({
        id: `TX-${m.id}`,
        desc: m.description || 'Movimiento bancario',
        amount: m.movement_type === 'expense' ? -Math.abs(parseFloat(m.amount) || 0) : Math.abs(parseFloat(m.amount) || 0),
        type: m.movement_type === 'expense' ? 'expense' as const : 'income' as const,
        date: m.movement_date || new Date().toISOString().split('T')[0],
        service: 'bank_movement'
      }));
      setTransactions(mappedTx);
    } catch (e) {
      console.error('Error fetching transactions from backend:', e);
    }
  };

  useEffect(() => {
    const initData = async () => {
      setIsLoadingData(true);
      const [accounts] = await Promise.all([
        fetchBankAccounts(),
        fetchClients(),
        fetchProducts()
      ]);
      await fetchTransactions(accounts.map(a => a.id));
      setIsLoadingData(false);
    };
    initData();
  }, []);

  const [productsList, setProductsList] = useState<ProductData[]>([]);

  const addInvoiceLine = () => {
    setInvoiceLines([...invoiceLines, { id: Math.random().toString(), product: null, quantity: 1, price: 0, vat: 4, customName: '' }]);
  };

  const removeInvoiceLine = (id: string) => {
    if (invoiceLines.length > 1) {
      setInvoiceLines(invoiceLines.filter(l => l.id !== id));
    } else {
      setInvoiceLines([{ id: '1', product: null, quantity: 1, price: 0, vat: 4, customName: '' }]);
    }
  };

  const handleProductChange = (lineId: string, productId: string) => {
    const prod = productsList.find(p => p.id === productId);
    setInvoiceLines(invoiceLines.map(line => {
      if (line.id === lineId) {
        if (prod) {
          return {
            ...line,
            product: prod,
            price: prod.defaultPrice,
            vat: prod.defaultVat,
            customName: prod.name
          };
        } else {
          return {
            ...line,
            product: null,
            price: 0,
            vat: 21,
            customName: ''
          };
        }
      }
      return line;
    }));
  };

  const handleLineFieldChange = (lineId: string, field: string, value: any) => {
    setInvoiceLines(invoiceLines.map(line => {
      if (line.id === lineId) {
        return { ...line, [field]: value };
      }
      return line;
    }));
  };

  const handleEmitInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClient) {
      notify('Error al emitir factura', 'Por favor, selecciona un cliente válido de la lista para emitir la factura.', 'warning');
      return;
    }

    const subtotal = invoiceLines.reduce((sum, l) => sum + (l.quantity * l.price), 0);
    const vatAmount = invoiceLines.reduce((sum, l) => sum + (l.quantity * l.price * l.vat / 100), 0);
    const totalFactura = subtotal + vatAmount;

    if (totalFactura <= 0) {
      notify('Error al emitir factura', 'El importe total de la factura debe ser mayor que cero.', 'warning');
      return;
    }

    const nextInvNum = `F-2026-${Math.floor(100 + Math.random() * 900)}`;
    const token = getAccessToken();

    // Backend Invoices API Integration: Crear factura y añadir líneas
    let createdInvoiceId: string | number = `INV-${Date.now()}`;
    if (token) {
      try {
        const clientIdParam = isNaN(Number(selectedClient.id)) ? '1' : String(selectedClient.id);
        const createUrl = `/invoices_create?access_token=${encodeURIComponent(token)}&company_id=${encodeURIComponent(getActiveCompanyId())}&client_id=${encodeURIComponent(clientIdParam)}&invoice_number=${encodeURIComponent(nextInvNum)}&subtotal=${encodeURIComponent(subtotal.toFixed(2))}&tax_amount=${encodeURIComponent(vatAmount.toFixed(2))}&total=${encodeURIComponent(totalFactura.toFixed(2))}&currency=EUR&status=draft`;
        let createRes = await ObexRequestsHandlerP80.get(createUrl);
        if (createRes?.data) createRes = createRes.data;
        if (createRes?.result) createRes = createRes.result;
        
        const returnedId = createRes?.response?.id || createRes?.id || (Array.isArray(createRes?.response) ? createRes.response[0]?.id : null);
        if (returnedId) {
          createdInvoiceId = returnedId;
        }

        // Añadir cada línea de factura al backend
        for (const line of invoiceLines) {
          const desc = line.customName || (line.product ? line.product.name : 'Artículo General');
          const lineTotal = (line.quantity * line.price).toFixed(2);
          const addLineUrl = `/invoice_lines_add?access_token=${encodeURIComponent(token)}&invoice_id=${encodeURIComponent(String(createdInvoiceId))}&description=${encodeURIComponent(desc)}&quantity=${encodeURIComponent(String(line.quantity))}&unit_price=${encodeURIComponent(String(line.price))}&discount_pct=0&tax_rate=${encodeURIComponent(String(line.vat))}&line_total=${encodeURIComponent(lineTotal)}`;
          await ObexRequestsHandlerP80.get(addLineUrl);
        }

        // Emitir Factura en Backend (invoices_issue pasa la factura a issued)
        const issueUrl = `/invoices_issue?access_token=${encodeURIComponent(token)}&id=${encodeURIComponent(String(createdInvoiceId))}`;
        await ObexRequestsHandlerP80.get(issueUrl);
      } catch (err) {
        console.error('Error emitting invoice via backend API:', err);
      }
    }

    // 1. Registrar transacción en QuickBooks (FinancesModule)
    const newTx = {
      id: `TX-${Math.floor(1000 + Math.random() * 9000)}`,
      desc: `Factura Emitida ${nextInvNum} a ${selectedClient.name}`,
      amount: totalFactura,
      type: 'income' as const,
      date: new Date().toISOString().split('T')[0],
      service: 'invoice'
    };
    setTransactions([newTx, ...transactions]);

    // 2. Generar el asiento contable correspondiente (asiento de doble entrada)
    const savedAsientosStr = localStorage.getItem('albatross_asientos_contables');
    let currentAsientos = savedAsientosStr ? JSON.parse(savedAsientosStr) : [];
    if (!Array.isArray(currentAsientos) || currentAsientos.length === 0) {
      currentAsientos = [ ...[] ];
    }

    // Calcular desglose de IVA para el asiento
    const vatBreakdown = invoiceLines.reduce((acc, l) => {
      const base = l.quantity * l.price;
      const vatValue = base * l.vat / 100;
      if (!acc[l.vat]) {
        acc[l.vat] = { base: 0, amount: 0 };
      }
      acc[l.vat].base += base;
      acc[l.vat].amount += vatValue;
      return acc;
    }, {} as Record<number, { base: number; amount: number }>);

    // Crear líneas de asiento
    const journalLines = [
      {
        account: '430000',
        accountName: 'Clientes (Euros)',
        debit: parseFloat(totalFactura.toFixed(2)),
        credit: 0
      },
      {
        account: '700000',
        accountName: 'Ventas de mercaderías',
        debit: 0,
        credit: parseFloat(subtotal.toFixed(2))
      }
    ];

    // Añadir línea de IVA por cada tipo de IVA aplicable
    Object.keys(vatBreakdown).forEach((vatRateKey) => {
      const vatRate = parseFloat(vatRateKey);
      const data = vatBreakdown[vatRate];
      if (data.amount > 0) {
        journalLines.push({
          account: '477000',
          accountName: `H.P. IVA Repercutido (${vatRate}%)`,
          debit: 0,
          credit: parseFloat(data.amount.toFixed(2))
        });
      }
    });

    const entryConcept = `FACTURA EMITIDA ${nextInvNum} A ${selectedClient.name.toUpperCase()}`;
    const newJournalEntry = {
      id: `AS-2026-${Math.floor(100 + Math.random() * 900)}`,
      date: new Date().toISOString().split('T')[0],
      concept: entryConcept,
      status: 'posted' as const,
      sourceModule: 'Factura Emitida',
      lines: journalLines
    };

    // Post to backend journal entries endpoint
    if (token) {
      try {
        const postEntryUrl = `/journal_entries_post?access_token=${encodeURIComponent(token)}&company_id=${encodeURIComponent(getActiveCompanyId())}&entry_date=${encodeURIComponent(newJournalEntry.date)}&concept=${encodeURIComponent(entryConcept)}&lines=${encodeURIComponent(JSON.stringify(journalLines))}`;
        await ObexRequestsHandlerP80.get(postEntryUrl);
      } catch (err) {
        console.error('Error posting journal entry to backend:', err);
      }
    }

    const updatedAsientos = [newJournalEntry, ...currentAsientos];
    localStorage.setItem('albatross_asientos_contables', JSON.stringify(updatedAsientos));

    // Guardar datos de la factura generada para la pantalla de visualización/impresión/envío
    const invoiceDetails = {
      invoiceId: createdInvoiceId,
      invoiceNumber: nextInvNum,
      date: new Date().toISOString().split('T')[0],
      client: selectedClient,
      lines: invoiceLines.map(l => ({
        sku: l.product ? l.product.sku : 'PROD-GEN',
        name: l.customName || (l.product ? l.product.name : 'Artículo General'),
        quantity: l.quantity,
        price: l.price,
        vat: l.vat,
        base: l.quantity * l.price,
        vatVal: l.quantity * l.price * l.vat / 100
      })),
      subtotal,
      vatAmount,
      totalFactura,
      paymentTerms: invoicePaymentTerms,
      bankAccount: invoiceBankAcc,
      vatBreakdown
    };

    setActiveInvoicePreview(invoiceDetails);
    
    // Resetear form
    setSelectedClient(null);
    setClientSearchQuery('');
    setInvoiceLines([{ id: '1', product: null, quantity: 1, price: 0, vat: 4, customName: '' }]);
    setActiveActionModal(null); // Cerrar modal de creación para mostrar el previsualizador
    
    notify('Factura emitida', `Factura ${nextInvNum} emitida, contabilizada y asiento contable generado en Libro Diario.`, 'info');
  };

  
  // Guardar favoritos
  useEffect(() => {
    localStorage.setItem('albatross_fin_favs', JSON.stringify(favorites));
  }, [favorites]);

  const toggleFavorite = (id: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Evitar que abra el modal
    if (favorites.includes(id)) {
      setFavorites(favorites.filter(fav => fav !== id));
    } else {
      if (favorites.length >= 8) {
        notify('Límite alcanzado', 'Solo puedes seleccionar un máximo de 8 accesos directos preferidos.', 'warning');
        return;
      }
      setFavorites([...favorites, id]);
    }
  };

  const handleCategoryChange = (category: string, label: string = category) => {
    if (!(document as any).startViewTransition) {
      setActiveCategoryFilter(category);
      notify('Filtro aplicado', `Filtrando panel financiero para métricas de tipo: ${label}`, 'info');
      return;
    }

    (document as any).startViewTransition(() => {
      return new Promise<void>(resolve => {
        setActiveCategoryFilter(category);
        notify('Filtro aplicado', `Filtrando panel financiero para métricas de tipo: ${label}`, 'info');
        // Give React 16 time to update the DOM
        setTimeout(resolve, 0);
      });
    });
  };

  // Cálculos dinámicos
  const totalIncome = transactions
    .filter(tx => tx.type === 'income')
    .reduce((sum, tx) => sum + tx.amount, 0);

  const totalExpense = Math.abs(
    transactions
      .filter(tx => tx.type === 'expense')
      .reduce((sum, tx) => sum + tx.amount, 0)
  );

  const netProfit = totalIncome - totalExpense;

  // Manejar el submit de cualquier formulario de acción
  const handleFormSubmit = async (actionId: string, formData: any) => {
    const amountVal = parseFloat(formData.amount) || 0;
    const isExpenseAction = ['expense', 'cheque', 'bill', 'pay_bills', 'pay_down_credit_card'].includes(actionId);
    const resolvedAmount = isExpenseAction ? -Math.abs(amountVal) : Math.abs(amountVal);
    const resolvedType = isExpenseAction ? 'expense' : 'income';
    const token = getAccessToken();
    const today = new Date().toISOString().split('T')[0];
    const desc = formData.description || `${QUICKBOOKS_ACTIONS.find(a => a.id === actionId)?.label} - ${formData.entity || 'General'}`;

    // Backend Bank Movements Import
    if (token) {
      try {
        const bankId = formData.bankAccount && !isNaN(parseInt(formData.bankAccount)) ? formData.bankAccount : '1';
        const importUrl = `/bank_movements_import?access_token=${encodeURIComponent(token)}&bank_account_id=${encodeURIComponent(bankId)}&description=${encodeURIComponent(desc)}&amount=${encodeURIComponent(String(amountVal))}&movement_type=${encodeURIComponent(resolvedType)}&movement_date=${encodeURIComponent(today)}`;
        await ObexRequestsHandlerP80.get(importUrl);

        // If it's a Bill or Expense, also record journal entry
        if (actionId === 'bill' || actionId === 'expense') {
          const expenseLines = isExpenseAction ? [
            { account: '600000', accountName: 'Compras / Gastos de Explotación', debit: amountVal, credit: 0 },
            { account: '400000', accountName: 'Proveedores / Acreedores', debit: 0, credit: amountVal }
          ] : [
            { account: '572000', accountName: 'Bancos c/c', debit: amountVal, credit: 0 },
            { account: '700000', accountName: 'Ventas e Ingresos', debit: 0, credit: amountVal }
          ];
          const postEntryUrl = `/journal_entries_post?access_token=${encodeURIComponent(token)}&company_id=${encodeURIComponent(getActiveCompanyId())}&entry_date=${encodeURIComponent(today)}&concept=${encodeURIComponent(desc)}&lines=${encodeURIComponent(JSON.stringify(expenseLines))}`;
          await ObexRequestsHandlerP80.get(postEntryUrl);
        }
      } catch (err) {
        console.error('Error importing bank movement or posting journal entry:', err);
      }
    }

    // 1. Agregar transacción
    const newTx = {
      id: `TX-${Math.floor(1000 + Math.random() * 9000)}`,
      desc: desc,
      amount: resolvedAmount,
      type: resolvedType as 'income' | 'expense',
      date: today,
      service: actionId
    };

    setTransactions([newTx, ...transactions]);

    // 2. Modificar saldos bancarios selectivamente
    if (formData.bankAccount) {
      setBankAccounts(prev => prev.map(acc => {
        if (acc.id === formData.bankAccount) {
          const newBal = acc.balance + (isExpenseAction ? -amountVal : amountVal);
          // Actualizar QuickBooks sincronizado
          return { ...acc, balance: newBal, qbBalance: newBal + (acc.qbBalance - acc.balance) };
        }
        return acc;
      }));
    }

    // Cerrar modal y notificar
    setActiveActionModal(null);
    notify('Acción registrada', `¡Acción "${QUICKBOOKS_ACTIONS.find(a => a.id === actionId)?.label}" registrada con éxito! El panel financiero se ha actualizado.`, 'info');
  };

  // Formateador de moneda sensible a privacidad
  const formatCurrency = (val: number, curr = 'EUR') => {
    if (privacyMode) return '•••••€';
    const symbol = curr === 'EUR' ? '€' : curr === 'USD' ? '$' : curr;
    return `${val.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${symbol}`;
  };

  // Filtrado de transacciones según la categoría seleccionada
  const filteredTransactions = transactions.filter(tx => {
    if (activeCategoryFilter === 'Expenses & Pay Bills') return tx.type === 'expense';
    if (activeCategoryFilter === 'Sales & Get Paid') return tx.type === 'income';
    return true;
  });

  return (
    <div className="finances-view-container">
      <ModuleHeader
        title="Control_Financiero"
        subtitle="Gestión contable, facturación y análisis de flujos de caja estilo QuickBooks."
      />



      {/* Barra de grupos */}
      <div className="finances-group-row">
        <ReactSortable
          className="finances-group-nav"
          list={orderedGroups}
          setList={items => saveNavOrder({ ...navOrder, groups: items.map(g => g.id) })}
          animation={150}
          forceFallback
          // Sin margen, 1px de temblor al pulsar se toma como arrastre y se pierde el clic.
          fallbackTolerance={15}
          scrollSensitivity={80}
          scrollSpeed={15}
          onStart={() => document.body.classList.add('sidebar-dragging')}
          onEnd={() => document.body.classList.remove('sidebar-dragging')}
        >
          {orderedGroups.map((group) => (
            <button
              key={group.id}
              onClick={() => handleSelectGroup(group.id)}
              className={activeGroup === group.id ? "active" : ""}
            >
              {group.name}
            </button>
          ))}
        </ReactSortable>
        <div className="finances-entries-counter">
          <span className="finances-entries-counter-dot" />
          <span>Nº asientos contabilizados:</span>
          {entriesCount === null ? <Skeleton width="28px" height="1.2em" /> : <strong>{entriesCount}</strong>}
        </div>
      </div>

      {/* Subpantallas del grupo activo */}
      {currentGroup?.subScreens && currentGroup.subScreens.length > 0 && (
        <ReactSortable
          className="finances-subscreen-nav"
          list={orderedSubScreens}
          setList={items => saveNavOrder({ ...navOrder, subScreens: { ...navOrder.subScreens, [activeGroup]: items.map(s => s.id) } })}
          animation={150}
          forceFallback
          // Sin margen, 1px de temblor al pulsar se toma como arrastre y se pierde el clic.
          fallbackTolerance={15}
          scrollSensitivity={80}
          scrollSpeed={15}
          onStart={() => document.body.classList.add('sidebar-dragging')}
          onEnd={() => document.body.classList.remove('sidebar-dragging')}
        >
          {orderedSubScreens.map((sub) => (
            <button
              key={sub.id}
              onClick={() => setActiveSubScreen(sub.id)}
              className={activeSubScreen === sub.id ? "active" : ""}
            >
              {sub.name}
            </button>
          ))}
        </ReactSortable>
      )}

      {/* Navegador de Categorías Principal style Quickbooks */}
      {activeGroup === 'panel_principal' && (
        <ReactSortable
          className="finances-category-nav"
          list={orderedCategories}
          setList={items => saveNavOrder({ ...navOrder, categories: items.map(c => c.id) })}
          animation={150}
          forceFallback
          // Sin margen, 1px de temblor al pulsar se toma como arrastre y se pierde el clic.
          fallbackTolerance={15}
          scrollSensitivity={80}
          scrollSpeed={15}
          onStart={() => document.body.classList.add('sidebar-dragging')}
          onEnd={() => document.body.classList.remove('sidebar-dragging')}
        >
          {orderedCategories.map((category) => (
            <button
              key={category.id}
              onClick={() => handleCategoryChange(category.id, category.label)}
              className={activeCategoryFilter === category.id ? "active" : ""}
            >
              {category.label}
            </button>
          ))}
        </ReactSortable>
      )}

      {/* BARRA DE ACCESOS DIRECTOS CUSTOMIZABLES (+ Botón contextual) */}
      {/*
      <div className="finances-quick-actions-bar">
        <div className="finances-quick-actions-col">
          <span className="finances-quick-actions-title">Acciones Rápidas_ [Atajos]</span>
          <div className="finances-quick-actions-list">
            {['invoice', 'expense', 'bank_deposit', 'cheque', 'bill', 'receive_payment'].map(favId => {
              const action = QUICKBOOKS_ACTIONS.find(a => a.id === favId);
              if (!action) return null;
              const Icon = action.icon;
              return (
                <button
                  key={favId}
                  onClick={() => openQuickAction(action)}
                  className="finances-action-btn group"
                >
                  <Icon size={12} className="finances-action-btn-icon" />
                  <span >{action.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Botón Mas (+) Contextual para Configuración de Accesos Rápidos 
        <div className="finances-quick-actions-right">
          <div className="finances-new-btn-wrapper">
            <button
              onClick={() => setIsNewMenuOpen(true)}
              className="finances-new-btn"
            >
              <Plus size={16} />
              Nuevas Acciones
            </button>
          </div>

          {/* MENÚ CONTEXTUAL SEGÚN REFERENCIA menu.jpeg 
          <>
            {isNewMenuOpen && (
              <>
                {/* Backdrop invisible para cerrar al hacer click fuera 
                <div
                  className="finances-new-menu-overlay"
                  onClick={() => setIsNewMenuOpen(false)}
                />

                <div className="finances-new-menu-container">
                  {/* Encabezado del menú 
                  <div className="finances-new-menu-header">
                    <div>
                      <h4 className="finances-new-menu-title">CREAR NUEVA ACCIÓN CONTABLE</h4>
                      <p className="finances-new-menu-subtitle">
                        Marca hasta 8 opciones con la estrella <Star size={10} className="finances-icon-yellow" /> para fijarlas en tu barra de accesos directos rápida.
                      </p>
                    </div>
                    <button
                      onClick={() => setIsNewMenuOpen(false)}
                      className="finances-new-menu-close"
                    >
                      <X size={16} />
                    </button>
                  </div>

                  {/* Cuerpo del menú en Tres Columnas según menu.jpeg 
                  <div className="finances-new-menu-grid">
                    {/* Columna Clientes 
                    <div className="finances-new-menu-col">
                      <div className="finances-new-menu-col-header">
                        <h4 className="finances-new-menu-col-title">Clientes</h4>
                      </div>
                      <div className="finances-new-menu-items">
                        {QUICKBOOKS_ACTIONS.filter(a => a.category === 'customers').map(action => {
                          const Icon = action.icon;
                          const isFav = favorites.includes(action.id);
                          return (
                            <div
                              key={action.id}
                              className="finances-new-menu-item"
                              onClick={() => {
                                openQuickAction(action);
                                setIsNewMenuOpen(false);
                              }}
                            >
                              <div className="finances-new-menu-item-left">
                                <Icon size={12} className="finances-new-menu-item-icon" />
                                <span className="finances-new-menu-item-text">{action.label}</span>
                              </div>
                              <button
                                onClick={(e) => toggleFavorite(action.id, e)}
                                className="finances-new-menu-item-fav"
                              >
                                <Star size={12} className={isFav ? "finances-icon-yellow" : "finances-icon-gray"} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Columna Proveedores 
                    <div className="finances-new-menu-col">
                      <div className="finances-new-menu-col-header">
                        <h4 className="finances-new-menu-col-title">Proveedores</h4>
                      </div>
                      <div className="finances-new-menu-items">
                        {QUICKBOOKS_ACTIONS.filter(a => a.category === 'suppliers').map(action => {
                          const Icon = action.icon;
                          const isFav = favorites.includes(action.id);
                          return (
                            <div
                              key={action.id}
                              className="finances-new-menu-item"
                              onClick={() => {
                                openQuickAction(action);
                                setIsNewMenuOpen(false);
                              }}
                            >
                              <div className="finances-new-menu-item-left">
                                <Icon size={12} className="finances-new-menu-item-icon" />
                                <span className="finances-new-menu-item-text">
                                  {action.label}
                                  {action.id === 'purchase_order' && <span className="finances-badge-amber">💎</span>}
                                </span>
                              </div>
                              <button
                                onClick={(e) => toggleFavorite(action.id, e)}
                                className="finances-new-menu-item-fav"
                              >
                                <Star size={12} className={isFav ? "finances-icon-yellow" : "finances-icon-gray"} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Columna Otros 
                    <div className="finances-new-menu-col">
                      <div className="finances-new-menu-col-header">
                        <h4 className="finances-new-menu-col-title">Otros</h4>
                      </div>
                      <div className="finances-new-menu-items">
                        {QUICKBOOKS_ACTIONS.filter(a => a.category === 'other').map(action => {
                          const Icon = action.icon;
                          const isFav = favorites.includes(action.id);
                          return (
                            <div
                              key={action.id}
                              className="finances-new-menu-item"
                              onClick={() => {
                                openQuickAction(action);
                                setIsNewMenuOpen(false);
                              }}
                            >
                              <div className="finances-new-menu-item-left">
                                <Icon size={12} className="finances-new-menu-item-icon" />
                                <span className="finances-new-menu-item-text">
                                  {action.label}
                                  {action.id === 'new_chat' && <span className="finances-badge-rose">●</span>}
                                </span>
                              </div>
                              <button
                                onClick={(e) => toggleFavorite(action.id, e)}
                                className="finances-new-menu-item-fav"
                              >
                                <Star size={12} className={isFav ? "finances-icon-yellow" : "finances-icon-gray"} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        </div>
      </div>
      */}

      {activeGroup !== 'panel_principal' ? (
        <>
          {/* Barra Volver a Acciones */}
          {currentQuickAction && (
            <div className="qa-back-bar">
              <div className="qa-back-bar-left">
                <button
                  type="button"
                  onClick={() => createOrigin
                    ? handleNavigate(createOrigin.group, createOrigin.subScreen)
                    : setActiveSubScreen('hub_acciones')}
                  className="qa-back-btn"
                >
                  <ArrowLeft size={14} /> Volver
                </button>
                <div>
                  <span className="qa-back-bar-category">{currentQuickAction.category}</span>
                  <h2 className="qa-back-bar-title">{currentQuickAction.name}</h2>
                </div>
              </div>
              <span className="qa-back-bar-hint">Pantalla de creación directa</span>
            </div>
          )}

          {activeSubScreen === 'hub_acciones' ? (
            <QuickActionsHub onSelectAction={setActiveSubScreen} />
          ) : SubScreenView ? (
            <SubScreenView
              privacyMode={privacyMode}
              totalIncome={totalIncome}
              totalExpense={totalExpense}
              onNavigate={handleNavigate}
              invoicePrefill={invoicePrefill}
              onConvertToInvoice={handleConvertToInvoice}
            />
          ) : suiteMenu ? (
            <>
              {activeSubScreen === 'facturas_emitidas' && (
                <div className="module-toolbar finances-subscreen-toolbar">
                  <span className="finances-subscreen-toolbar-text">Facturas emitidas a clientes</span>
                  <button
                    type="button"
                    className="module-create-btn"
                    onClick={() => handleNavigate('deudores', 'crear_factura_emitida')}
                  >
                    <Plus size={14} /> Emitir_Nueva_Factura
                  </button>
                </div>
              )}
            <FinancialSuite
              privacyMode={privacyMode}
              activeMenu={suiteMenu}
              onChangeMenu={handleSuiteMenuChange}
              hideSidebar
              headerTitle={currentSubScreen?.name}
              headerDesc={currentSubScreen?.desc}
              taxModel={activeSubScreen === 'modelo_303' ? '303' : activeSubScreen === 'modelo_111' ? '111' : undefined}
            />
            </>
          ) : (
            <div className="finances-placeholder">
              <span className="finances-placeholder-tag">EN PREPARACIÓN</span>
              <h3 className="finances-placeholder-title">{currentSubScreen?.name}</h3>
              <p className="finances-placeholder-desc">{currentSubScreen?.desc}</p>
            </div>
          )}
        </>
      ) : (
        <>
          {/* Cabecera del panel principal */}
          <div className="finances-welcome-card">
            <div className="finances-welcome-left">
              <div className="finances-welcome-icon">
                <Sparkles size={18} />
              </div>
              <div>
                <h3 className="finances-welcome-title">{dashboardGreeting} Panel General de Finanzas</h3>
                <p className="finances-welcome-desc">Control presupuestario, contabilidad PGC, facturación y estados de tesorería en tiempo real</p>
              </div>
            </div>
            <div className="finances-welcome-status">
              <span className="finances-welcome-dot" />
              <span>SISTEMA CONTABLE OPERATIVO</span>
            </div>
          </div>

          {/* DASHBOARD WIDGETS GRID al estilo finances.jpeg */}
      <div className="finances-widgets-grid">

        {/* WIDGET 1: PROFIT & LOSS */}
        {isWidgetVisible('profit_loss') && (
          <div className="finances-widget-card" style={{ viewTransitionName: 'widget-profit-loss' } as any}>
            <div>
              <div className="finances-widget-header">
                <div>
                  <h3 className="finances-widget-title">PÉRDIDAS Y GANANCIAS</h3>
                  <p className="finances-widget-subtitle">Beneficio neto del periodo</p>
                </div>
                <select
                  value={pnlPeriod}
                  onChange={(e: any) => {
                    setPnlPeriod(e.target.value);
                    notify('Periodo actualizado', 'Periodo de Pérdidas y Ganancias actualizado.', 'info');
                  }}
                  className="finances-widget-select"
                >
                  <option value="last_month">Último mes</option>
                  <option value="last_year">Último año</option>
                </select>
              </div>

              {/* Total Balance */}
              <div className="finances-widget-balance-group">
                <div className="finances-new-menu-item-left">
                  {isLoadingData ? (
                    <Skeleton width="120px" height="1.2em" className="finances-widget-balance-amount" />
                  ) : (
                    <span className={`finances-widget-balance-amount ${netProfit >= 0 ? "green" : "red"}`}>
                      {formatCurrency(pnlPeriod === 'last_month' ? netProfit : netProfit * 4.2)}
                    </span>
                  )}
                  <span className="finances-widget-balance-badge green">
                    100%
                  </span>
                </div>
                <div className="finances-widget-trend green">
                  <TrendingUp size={12} />
                  <span >+52% respecto al mes anterior</span>
                </div>
              </div>

              {/* Graphical Balance details */}
              <div className="finances-widget-bar-group">
                {/* Ingresos */}
                <div>
                  <div className="finances-widget-bar-header">
                    <span className="finances-widget-bar-label">INGRESOS</span>
                    {isLoadingData
                      ? <Skeleton width="72px" height="1.2em" className="finances-widget-bar-value" />
                      : <span className="finances-widget-bar-value">{formatCurrency(totalIncome)}</span>}
                  </div>
                  <div className="finances-widget-bar-bg">
                    <div
                      className="finances-widget-bar-fill green"
                      style={{ width: `${totalIncome > 0 ? Math.min(100, (totalIncome / (totalIncome + totalExpense || 1)) * 100) : 0}%` }}
                    />
                  </div>
                </div>

                {/* Gastos */}
                <div>
                  <div className="finances-widget-bar-header">
                    <span className="finances-widget-bar-label">GASTOS</span>
                    {isLoadingData
                      ? <Skeleton width="72px" height="1.2em" className="finances-widget-bar-value" />
                      : <span className="finances-widget-bar-value">{formatCurrency(totalExpense)}</span>}
                  </div>
                  <div className="finances-widget-bar-bg">
                    <div
                      className="finances-widget-bar-fill teal"
                      style={{ width: `${totalExpense > 0 ? Math.min(100, (totalExpense / (totalIncome + totalExpense || 1)) * 100) : 100}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="finances-widget-footer">
              <button
                onClick={() => handleNavigate('balances', 'pyg')}
                className="finances-link-btn"
              >
                Ver informe de pérdidas y ganancias
              </button>
              <div className="finances-widget-controls">
                <button className="finances-widget-icon-btn" title="Opciones">
                  <SlidersHorizontal size={12} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* WIDGET 2: EXPENSES (Donut Chart representation) */}
        {isWidgetVisible('expenses') && (
          <div className="finances-widget-card" style={{ viewTransitionName: 'widget-expenses' } as any}>
            <div>
              <div className="finances-widget-header">
                <div>
                  <h3 className="finances-widget-title">GASTOS</h3>
                  <p className="finances-widget-subtitle">Gastos en los últimos 30 días</p>
                </div>
                <select
                  value={expensePeriod}
                  onChange={(e: any) => {
                    setExpensePeriod(e.target.value);
                    notify('Filtro actualizado', 'Filtro de gastos actualizado.', 'info');
                  }}
                  className="finances-widget-select"
                >
                  <option value="30_days">Últimos 30 días</option>
                  <option value="90_days">Últimos 90 días</option>
                </select>
              </div>

              {/* Balance */}
              <div className="finances-widget-balance-row">
                <div className="finances-widget-title-group">
                  {isLoadingData ? (
                    <Skeleton width="120px" height="1.2em" className="finances-widget-balance-amount" />
                  ) : (
                    <span className="finances-widget-balance-amount bright">
                      {formatCurrency(totalExpense)}
                    </span>
                  )}
                  <span className="finances-widget-trend red">Gastos totales acumulados</span>
                </div>
                <div className="finances-widget-balance-badge red">
                  {totalExpense > 0 ? '100%' : '0%'}
                </div>
              </div>

              {/* Pie/Donut Chart representation with Hover Effects */}
              {(() => {
                const expenseTxs = transactions.filter(t => t.type === 'expense');
                if (isLoadingData) {
                  return (
                    <div className="finances-donut-hidden" style={{ padding: '30px 10px' }}>
                      <Skeleton width="100%" height="90px" />
                    </div>
                  );
                }
                if (expenseTxs.length === 0) {
                  return (
                    <div className="finances-donut-hidden" style={{ padding: '30px 10px', textAlign: 'center' }}>
                      No hay gastos registrados en el periodo seleccionado.
                    </div>
                  );
                }

                // Group expenses by concept
                const catMap: Record<string, number> = {};
                expenseTxs.forEach(t => {
                  const key = t.desc || 'Otros gastos';
                  catMap[key] = (catMap[key] || 0) + Math.abs(t.amount);
                });

                const donutData = Object.entries(catMap).slice(0, 5).map(([name, val]) => ({
                  name,
                  value: val
                }));

                return (
                  <div className="finances-donut-container">
                    <div className="finances-donut-chart">
                      {!privacyMode ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={donutData}
                              cx="50%"
                              cy="50%"
                              innerRadius={35}
                              outerRadius={50}
                              paddingAngle={3}
                              dataKey="value"
                            >
                              {donutData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={DONUT_COLORS[index % DONUT_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip
                              contentStyle={{ background: '#1a1a1a', border: '1px solid #334155' }}
                              labelStyle={{ color: '#fff', fontSize: '10px', fontFamily: 'monospace' }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="finances-donut-hidden">Información oculta</div>
                      )}
                      <div className="finances-donut-overlay">
                        <span className="finances-donut-overlay-text">PGC</span>
                        <span className="finances-donut-overlay-sub">VIVO</span>
                      </div>
                    </div>

                    {/* Legend list */}
                    <div className="finances-donut-legend">
                      {donutData.map((item, id) => (
                        <div key={id} className="finances-donut-legend-item">
                          <div className="finances-donut-legend-left">
                            <div className="finances-donut-legend-dot" style={{ backgroundColor: DONUT_COLORS[id % DONUT_COLORS.length] }} />
                            <span className="finances-donut-legend-name">{item.name}</span>
                          </div>
                          <div className="finances-donut-legend-right">
                            <span className="finances-widget-bar-value">{formatCurrency(item.value)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="finances-widget-footer">
              {/* Gastos directos ya no está en el menú; se deja por si vuelve
              <button
                onClick={() => handleNavigate('compras_pagos', 'gastos')}
                className="finances-link-btn"
              >
                Categorizar transacciones ({transactions.filter(t => t.type === 'expense').length})
              </button>
              */}
              <button className="finances-widget-icon-btn" title="Exportar">
                <RefreshCw size={12} />
              </button>
            </div>
          </div>
        )}

        {/* WIDGET 3: BANK ACCOUNTS (Wise detail according to finances.jpeg) */}
        {isWidgetVisible('bank_accounts') && (
          <div className="finances-widget-card" style={{ viewTransitionName: 'widget-bank-accounts' } as any}>
            <div>
              <div className="finances-widget-header">
                <div>
                  <h3 className="finances-widget-title">CUENTAS BANCARIAS</h3>
                  <p className="finances-widget-subtitle">Actualizado en vivo</p>
                </div>
                <button
                  onClick={async () => {
                    notify('Sincronización bancaria', 'Sincronizando cuentas y transacciones bancarias en vivo...', 'info');
                    const accounts = await fetchBankAccounts();
                    await fetchTransactions(accounts.map(a => a.id));
                  }}
                  className="finances-widget-icon-btn green"
                  title="Sincronizar"
                >
                  <RefreshCw size={12} />
                </button>
              </div>

              {/* Registry listing style finances.jpeg */}
              <div className="finances-new-menu-col">
                {isLoadingData ? (
                  <>
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="finances-bank-item">
                        <div className="finances-bank-icon">
                          <Skeleton width="16px" height="16px" />
                        </div>
                        <div className="finances-bank-content">
                          <div className="finances-bank-top">
                            <Skeleton width="72px" height="1.2em" className="finances-bank-name" />
                            <div className="finances-bank-balances">
                              <Skeleton width="104px" height="1.2em" className="finances-bank-bal" />
                              <Skeleton width="88px" height="1.2em" className="finances-bank-qb-bal" />
                            </div>
                          </div>
                          <div className="finances-bank-bottom">
                            <Skeleton width="88px" height="1.2em" />
                            <Skeleton width="52px" height="1.2em" className="finances-bank-updated" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                ) : bankAccounts.length === 0 ? (
                  <div className="finances-donut-hidden" style={{ padding: '20px 0', textAlign: 'center' }}>
                    No se han encontrado cuentas bancarias registradas en el sistema.
                  </div>
                ) : (
                  bankAccounts.map((account) => (
                    <div key={account.id} className="finances-bank-item group">
                      <div className="finances-bank-icon">
                        <Building2 size={16} />
                      </div>
                      <div className="finances-bank-content">
                        <div className="finances-bank-top">
                          <span className="finances-bank-name">{account.name}</span>
                          <div className="finances-bank-balances">
                            <span className="finances-bank-bal">Banco: <strong className="finances-text-white">{formatCurrency(account.balance, account.currency)}</strong></span>
                            <span className="finances-bank-qb-bal">En contabilidad: <strong className="finances-smart-sugs-icon">{formatCurrency(account.qbBalance, account.currency)}</strong></span>
                          </div>
                        </div>

                        {/* Notificación de atencion necesaria o sincronizada */}
                        <div className="finances-bank-bottom">
                          {account.alert ? (
                            <div className="finances-bank-status warning">
                              <AlertCircle size={10} />
                              <span className="finances-bank-status-text">Requiere atención</span>
                            </div>
                          ) : (
                            <div className="finances-bank-status ok">
                              <CheckCircle size={10} />
                              <span className="finances-bank-status-text">Sincronizado y correcto</span>
                            </div>
                          )}
                          <span className="finances-bank-updated">En línea</span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="finances-widget-footer">
              <button
                onClick={() => handleNavigate('tesoreria_bancos', 'conciliacion_bancaria')}
                className="finances-link-btn"
              >
                Ir a conciliación bancaria <ChevronRight size={10} />
              </button>
              <button
                onClick={() => notify('Banca abierta', 'Configurar conexiones API para banca abierta (Open Banking) con Token SEPA.', 'info')}
                className="finances-widget-icon-btn"
              >
                <Settings size={12} />
              </button>
            </div>
          </div>
        )}

        {/* WIDGET 4: CASH FLOW (Takes full row or spans wide) */}
        {isWidgetVisible('cash_flow') && (
          <div className="finances-widget-card finances-widget-col-2" style={{ viewTransitionName: 'widget-cash-flow' } as any}>
            <div>
              <div className="finances-cf-header">
                <div>
                  <h3 className="finances-widget-title">FLUJO DE CAJA</h3>
                  <p className="finances-widget-subtitle">Actualizado en vivo</p>
                </div>

                {/* Double Toggle Tabs */}
                <div className="finances-cf-tabs">
                  <button
                    onClick={() => setCashFlowTab('balance')}
                    className={`finances-cf-tab ${cashFlowTab === 'balance' ? 'active' : ''}`}
                  >
                    Saldo de caja
                  </button>
                  <button
                    onClick={() => setCashFlowTab('money')}
                    className={`finances-cf-tab ${cashFlowTab === 'money' ? 'active' : ''}`}
                  >
                    Entradas y salidas
                  </button>
                </div>
              </div>

              {/* Total Balance info panel */}
              {(() => {
                const totalCash = bankAccounts.reduce((sum, a) => sum + a.balance, 0) || (totalIncome - totalExpense);
                return (
                  <div className="finances-widget-balance-row">
                    <div className="finances-widget-title-group">
                      <span className="finances-cf-info-label">Saldo de caja hoy</span>
                      {isLoadingData ? (
                        <Skeleton width="120px" height="1.2em" className="finances-widget-balance-amount" />
                      ) : (
                        <span className="finances-widget-balance-amount bright">
                          {formatCurrency(totalCash)}
                        </span>
                      )}
                    </div>
                    <div className="finances-cf-info-right">
                      <span>Proyección de Tesorería</span>
                      {isLoadingData
                        ? <Skeleton width="88px" height="1.2em" className="finances-cf-info-proj" />
                        : <div className="finances-cf-info-proj">{netProfit >= 0 ? '+ Positivo' : '- En revisión'}</div>}
                    </div>
                  </div>
                );
              })()}

              {/* Graphic Chart in Area / Bar option */}
              <div className="finances-cf-chart">
                {isLoadingData ? (
                  <Skeleton width="100%" height="100%" />
                ) : !privacyMode ? (
                  cashFlowTab === 'balance' ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={[
                        { name: 'Saldo Anterior', balance: bankAccounts.reduce((sum, a) => sum + a.balance, 0) || 0 },
                        { name: 'Entradas', balance: totalIncome },
                        { name: 'Salidas', balance: totalExpense },
                        { name: 'Actual', balance: (bankAccounts.reduce((sum, a) => sum + a.balance, 0) || (totalIncome - totalExpense)) }
                      ]}>
                        <defs>
                          <linearGradient id="colorBal" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#00ab84" stopOpacity={0.4} />
                            <stop offset="95%" stopColor="#00ab84" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <XAxis dataKey="name" stroke="#475569" fontSize={9} tickLine={false} />
                        <YAxis stroke="#475569" fontSize={9} tickLine={false} />
                        <Tooltip contentStyle={{ background: '#1a1a1a', border: '1px solid #334155' }} />
                        <Area type="monotone" dataKey="balance" name="Saldo" stroke="#00ab84" strokeWidth={2} fillOpacity={1} fill="url(#colorBal)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={[
                        { name: 'Periodo Actual', Ingresos: totalIncome, Gastos: totalExpense }
                      ]}>
                        <XAxis dataKey="name" stroke="#475569" fontSize={9} tickLine={false} />
                        <YAxis stroke="#475569" fontSize={9} tickLine={false} />
                        <Tooltip contentStyle={{ background: '#1a1a1a', border: '1px solid #334155' }} />
                        <Bar dataKey="Ingresos" fill="#00ab84" />
                        <Bar dataKey="Gastos" fill="#ff007f" />
                      </BarChart>
                    </ResponsiveContainer>
                  )
                ) : (
                  <div className="finances-cf-hidden">Información confidencial oculta</div>
                )}
              </div>
            </div>

            {/* Banner de Ayuda superior / inferior */}
            <div className="finances-cf-footer">
              <div className="finances-new-menu-item-left">
                <Sparkles size={12} className="finances-cf-learn-icon" />
                <span>Conozca el planificador de flujo de caja.</span>
                <button
                  onClick={() => notify('Tesorería', 'Abriendo documentación interactiva de estimación de tesorería...', 'info')}
                  className="finances-link-btn"
                >
                  Saber más
                </button>
              </div>
              <button
                onClick={() => notify('Flujo de caja', 'Generando proyección de flujo de caja...', 'info')}
                className="finances-link-btn"
              >
                Ver flujo de caja
              </button>
            </div>
          </div>
        )}

        {/* WIDGET 5: ADD WIDGETS / SMART SUGGESTIONS (Interactive box) */}
        <div className="finances-smart-widget" style={{ viewTransitionName: 'widget-smart-sugs' } as any}>
          <div className="finances-new-menu-col">
            <span className="finances-smart-header">Añadir Módulos</span>

            <div className="finances-smart-center">
              <button
                onClick={() => {
                  if (activeWidgets.length < 4) {
                    setActiveWidgets(['profit_loss', 'expenses', 'cash_flow', 'bank_accounts']);
                    notify('Widgets', 'Widgets reiniciados a la vista predeterminada.', 'info');
                  } else {
                    notify('Widgets', 'Todos los widgets clave ya se encuentran activos en la pantalla frontal.', 'info');
                  }
                }}
                className="finances-smart-add-btn"
              >
                <Plus size={20} />
              </button>
            </div>

            {/* Smart Suggestions list */}
            <div className="finances-smart-sugs">
              <div className="finances-smart-sugs-title">
                <Sparkles size={10} className="finances-smart-sugs-icon" />
                <span>Sugerencias inteligentes</span>
              </div>
              <div className="finances-smart-sugs-list">
                {[
                  { label: 'Ventas', target: 'deudores' },
                  { label: 'Cuentas por cobrar', target: 'deudores' },
                  { label: 'Impuestos trimestrales', target: 'impuestos_aeat' }
                ].map((sug) => (
                  <button
                    key={sug.label}
                    onClick={() => {
                      notify('Sugerencia aplicada', `Navegando a optimización de: ${sug.label}`, 'info');
                      handleNavigate(sug.target);
                    }}
                    className="finances-smart-sug-btn"
                  >
                    {sug.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="finances-smart-why">
            <span onClick={() => notify('Sugerencias', 'Esta sección recopila inputs financieros para sugerirte mejoras en el flujo de caja e IVA.', 'info')}>¿Por qué veo estas sugerencias?</span>
          </div>
        </div>

      </div>

      {/* SECCIÓN ESPECIAL DE IMPUESTOS (Módulo Tax) */}
      {activeCategoryFilter === 'Tax' && (
        <div>
          {/* Tarjeta IVA Repercutido / Soportado */}
          <div className="finances-tax-card">
            <div className="finances-widget-header">
              <div>
                <h3 className="finances-widget-title">IMPUESTOS & IVA (Modelo 303)</h3>
                <p className="finances-widget-subtitle">Estimación acumulada Trimestre Actual</p>
              </div>
              <div className="finances-widget-controls">
                <span className="finances-tax-badge">AL CORRIENTE</span>
              </div>
            </div>

            <div className="finances-tax-stats-grid">
              <div className="finances-tax-stat-box">
                <span className="finances-tax-stat-label">IVA Repercutido (Ventas)</span>
                <p className="finances-tax-stat-value green">{formatCurrency(totalIncome * 0.21)}</p>
                <p className="finances-tax-stat-desc">Calculado al 21% estándar</p>
              </div>
              <div className="finances-tax-stat-box">
                <span className="finances-tax-stat-label">IVA Soportado (Gastos)</span>
                <p className="finances-tax-stat-value red">{formatCurrency(totalExpense * 0.21)}</p>
                <p className="finances-tax-stat-desc">Deducible estimado promedio</p>
              </div>
              <div className="finances-tax-stat-box">
                <span className="finances-tax-stat-label">Liquidación Estimada</span>
                <p className="finances-tax-stat-value white">{formatCurrency((totalIncome - totalExpense) * 0.21)}</p>
                <p className="finances-tax-stat-desc green">A pagar (Hacienda España)</p>
              </div>
            </div>

            <div className="finances-tax-breakdown-group">
              <span className="finances-tax-breakdown-title">Desglose de Base Imponible</span>
              <div className="finances-tax-breakdown-row bordered">
                <span className="finances-tax-breakdown-label">Base Imponible Ventas</span>
                <span className="finances-widget-bar-value">{formatCurrency(totalIncome)}</span>
              </div>
              <div className="finances-tax-breakdown-row bordered">
                <span className="finances-tax-breakdown-label">Base Imponible Compras</span>
                <span className="finances-widget-bar-value">{formatCurrency(totalExpense)}</span>
              </div>
              <div className="finances-tax-breakdown-row">
                <span className="finances-tax-breakdown-label">Retenciones IRPF (Acreedores)</span>
                <span className="finances-tax-breakdown-val amber">{formatCurrency(totalExpense * 0.15)}</span>
              </div>
            </div>
          </div>

          {/* Próximos Vencimientos Fiscales */}
          <div className="finances-widget-card">
            <div>
              <h3 className="finances-vencimientos-title">Vencimientos_Fiscales</h3>

              <div className="finances-new-menu-col">
                <div className="finances-vencimiento-item urgent">
                  <div className="finances-vencimiento-date urgent">
                    20 <span className="finances-vencimiento-month">JUN</span>
                  </div>
                  <div>
                    <span className="finances-vencimiento-name">Modelo 111 (Retenciones)</span>
                    <span className="finances-vencimiento-status urgent">URGENTE • FALTA GENERAR</span>
                  </div>
                </div>

                <div className="finances-vencimiento-item normal">
                  <div className="finances-vencimiento-date normal">
                    20 <span className="finances-vencimiento-month">JUL</span>
                  </div>
                  <div>
                    <span className="finances-vencimiento-name">Modelo 303 (Trimestral IVA)</span>
                    <span className="finances-vencimiento-status normal">Aprobado por el gestor</span>
                  </div>
                </div>

                <div className="finances-vencimiento-item normal">
                  <div className="finances-vencimiento-date normal">
                    20 <span className="finances-vencimiento-month">JUL</span>
                  </div>
                  <div>
                    <span className="finances-vencimiento-name">Modelo 130 (Pago Fraccionado)</span>
                    <span className="finances-vencimiento-status normal">Aprobado por el gestor</span>
                  </div>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => notify('Modelos AEAT', 'Generando borrador XML oficial para la AEAT en descargas...', 'info')}
              className="finances-export-btn"
            >
              Exportar Modelos AEAT
            </button>
          </div>
        </div>
      )}

      {/* HISTORIAL / TRANSACCIONES RECIENTES */}
      <div className="finances-history-card">
        <div className="finances-widget-balance-row">
          <div>
            <h3 className="finances-widget-title">Transacciones_Recientes</h3>
            <p className="finances-widget-subtitle">Últimos movimientos financieros registrados en Albatross</p>
          </div>
          <button
            onClick={() => handleNavigate('balances', 'review_entries')}
            className="finances-link-btn"
          >
            Ver Todo el Historial
          </button>
        </div>

        <div className="finances-history-grid">
          {isLoadingData ? (
            <>
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="finances-history-item">
                  <div className="finances-history-item-left">
                    <Skeleton width="28px" height="28px" />
                    <div className="skeleton-block skeleton-block--tight" style={{ flex: 1 }}>
                      <Skeleton width="70%" height="1.2em" />
                      <Skeleton width="45%" height="1.2em" />
                    </div>
                  </div>
                  <Skeleton width="72px" height="1.2em" />
                </div>
              ))}
            </>
          ) : filteredTransactions.length === 0 ? (
            <div className="finances-donut-hidden" style={{ padding: '24px', textAlign: 'center', gridColumn: '1 / -1' }}>
              No hay movimientos bancarios registrados en la base de datos.
            </div>
          ) : (
            filteredTransactions.slice(0, 10).map((tx, idx) => (
              <div key={idx} className="finances-history-item">
                <div className="finances-history-item-left">
                  <div className={`finances-history-icon ${tx.type === 'income' ? 'income' : 'expense'}`}>
                    {tx.type === 'income' ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                  </div>
                  <div>
                    <p className="finances-history-desc">{tx.desc}</p>
                    <span className="finances-history-meta">{tx.id} | {tx.date}</span>
                  </div>
                </div>
                <span className={`finances-history-amount ${tx.type === 'income' ? 'income' : 'expense'}`}>
                  {formatCurrency(tx.amount)}
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Alertas Financieras */}
      <div className="finances-alert-card">
        <AlertCircle size={16} className="finances-alert-icon" />
        <div>
          <span className="finances-alert-title">Alertas_Financieras</span>
          <p className="finances-alert-desc">
            Se han detectado <span className="finances-alert-highlight">3 facturas</span> pendientes de cobro con más de 30 días de antigüedad. Un desfase de conciliación activa de <span className="finances-alert-highlight">936,72€</span> en la cuenta WISE EUR de QuickBooks necesita revisión de saldos.
          </p>
        </div>
      </div>

              </>
      )}

      {/* MODALES INTERACTIVOS PARA LAS ACCIONES (Formularios dinámicos y funcionales) */}
      <>
        {activeActionModal && (
          <div 
            className="finances-modal-overlay"
            onClick={handleRequestCloseActionModal}
          >
            <div 
              className={`finances-modal-container ${activeActionModal.id === 'invoice' ? 'finances-modal-container-wide' : ''}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="finances-modal-header">
                <div className="finances-new-menu-item-left">
                  <span className="finances-modal-icon-wrapper">
                    {React.createElement(activeActionModal.icon, { size: 16 })}
                  </span>
                  <div>
                    <h3 className="finances-modal-title">Formulario: {activeActionModal.label}</h3>
                    <p className="finances-tax-stat-label">{activeActionModal.description}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleRequestCloseActionModal}
                  className="finances-new-menu-close"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Formulario Dinámico y funcional */}
              {activeActionModal.id === 'invoice' ? (
                <form onSubmit={handleEmitInvoice} className="fv-invoice-1">
                  {/* Búsqueda rápida de clientes */}
                  <div className="fv-invoice-2">
                    <label className="fv-invoice-3">
                      Identificar Cliente <span className="fv-invoice-4">*</span>
                    </label>
                    <div className="fv-invoice-5">
                      <Search size={14} className="fv-invoice-6" />
                      <input
                        type="text"
                        placeholder="Buscar cliente por nombre o NIF (escribe para filtrar)..."
                        value={clientSearchQuery}
                        onChange={(e) => {
                          setClientSearchQuery(e.target.value);
                          setShowClientDropdown(true);
                        }}
                        onFocus={() => setShowClientDropdown(true)}
                        className="fv-invoice-7"
                      />
                      {clientSearchQuery && (
                        <button
                          type="button"
                          onClick={() => {
                            setClientSearchQuery('');
                            setSelectedClient(null);
                          }}
                          className="fv-invoice-8"
                        >
                          <X size={12} />
                        </button>
                      )}
                    </div>

                    {/* Dropdown de Clientes */}
                    
                      {showClientDropdown && (
                        <>
                          <div className="fv-invoice-9" onClick={() => setShowClientDropdown(false)} />
                          <div className="fv-invoice-10"
                          >
                            {clientsList.filter(c => 
                              c.name.toLowerCase().includes(clientSearchQuery.toLowerCase()) ||
                              c.cif.toLowerCase().includes(clientSearchQuery.toLowerCase()) ||
                              c.contactPerson.toLowerCase().includes(clientSearchQuery.toLowerCase())
                            ).map(client => (
                              <button
                                key={client.id}
                                type="button"
                                onClick={() => {
                                  setSelectedClient(client);
                                  setClientSearchQuery(client.name);
                                  setShowClientDropdown(false);
                                }}
                                className="fv-invoice-11"
                              >
                                <div className="fv-invoice-12">
                                  <span>{client.name}</span>
                                  <span className="fv-invoice-4">{client.cif}</span>
                                </div>
                                <div className="fv-invoice-13">
                                  Contacto: {client.contactPerson} ({client.email})
                                </div>
                              </button>
                            ))}
                            {clientsList.filter(c => 
                              c.name.toLowerCase().includes(clientSearchQuery.toLowerCase()) ||
                              c.cif.toLowerCase().includes(clientSearchQuery.toLowerCase()) ||
                              c.contactPerson.toLowerCase().includes(clientSearchQuery.toLowerCase())
                            ).length === 0 && (
                              <div className="fv-invoice-14">
                                Ningún cliente coincide con la búsqueda
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    

                    {/* Ficha rápida de cliente seleccionado */}
                    {selectedClient && (
                      <div className="fv-invoice-15"
                      >
                        <div>
                          <p className="fv-invoice-16">{selectedClient.name}</p>
                          <p className="fv-invoice-17">NIF/CIF: <span className="fv-invoice-18">{selectedClient.cif}</span> | Tel: <span className="fv-invoice-18">{selectedClient.phone}</span></p>
                          <p className="fv-invoice-19">Dirección: <span className="fv-invoice-18">{selectedClient.address}</span></p>
                        </div>
                        <div className="fv-invoice-20">
                          <p className="fv-invoice-21">Persona de Contacto</p>
                          <p className="fv-invoice-22">{selectedClient.contactPerson}</p>
                          <p className="fv-invoice-23">{selectedClient.email}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Líneas de productos (Multi-línea) */}
                  <div className="fv-invoice-24">
                    <div className="fv-invoice-25">
                      <label className="fv-invoice-26">
                        Líneas de Factura (Artículos / Servicios)
                      </label>
                      <button
                        type="button"
                        onClick={addInvoiceLine}
                        className="fv-invoice-27"
                      >
                        + Añadir Línea
                      </button>
                    </div>

                    <div className="fv-invoice-28">
                      {invoiceLines.map((line) => (
                        <div
                          key={line.id}
                          className="fv-invoice-29"
                        >
                          <div className="fv-invoice-30">
                            
                            {/* Selector de Producto */}
                            <div className="fv-invoice-31">
                              <span className="fv-invoice-32">Artículo / Catálogo</span>
                              <select
                                value={line.product?.id || ''}
                                onChange={(e) => handleProductChange(line.id, e.target.value)}
                                className="fv-invoice-33"
                              >
                                <option value="">[ Selecciona artículo catálogo ]</option>
                                {productsList.map(p => (
                                  <option key={p.id} value={p.id}>{p.name} ({p.defaultPrice}€)</option>
                                ))}
                                <option value="custom">[ Escribir manual / Concepto personalizado ]</option>
                              </select>
                            </div>

                            {/* Nombre del concepto */}
                            <div className="fv-invoice-34">
                              <span className="fv-invoice-32">Descripción Línea</span>
                              <input
                                type="text"
                                value={line.customName}
                                onChange={(e) => handleLineFieldChange(line.id, 'customName', e.target.value)}
                                placeholder="Escribe descripción..."
                                className="fv-invoice-33"
                              />
                            </div>

                            {/* Cantidad */}
                            <div className="fv-invoice-35">
                              <span className="fv-invoice-32">Cant.</span>
                              <input
                                type="number"
                                min="1"
                                step="1"
                                value={line.quantity}
                                onChange={(e) => handleLineFieldChange(line.id, 'quantity', parseInt(e.target.value) || 1)}
                                className="fv-invoice-36"
                              />
                            </div>

                            {/* Precio unitario */}
                            <div className="fv-invoice-37">
                              <span className="fv-invoice-32">Precio Unit.</span>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={line.price}
                                onChange={(e) => handleLineFieldChange(line.id, 'price', parseFloat(e.target.value) || 0)}
                                className="fv-invoice-38"
                              />
                            </div>

                            {/* Selector de IVA */}
                            <div className="fv-invoice-35">
                              <span className="fv-invoice-32">IVA</span>
                              <select
                                value={line.vat}
                                onChange={(e) => handleLineFieldChange(line.id, 'vat', parseInt(e.target.value) || 0)}
                                className="fv-invoice-33"
                              >
                                <option value="0">0%</option>
                                <option value="4">4%</option>
                                <option value="10">10%</option>
                                <option value="21">21%</option>
                              </select>
                            </div>

                          </div>

                          {/* Línea Total y eliminar */}
                          <div className="fv-invoice-39">
                            <span className="fv-invoice-40">
                              SKU/Cód: <span className="fv-invoice-41">{line.product ? line.product.sku : 'PROD-GEN'}</span>
                            </span>
                            <div className="fv-invoice-42">
                              <span className="fv-invoice-40">
                                Base: <span className="fv-invoice-18">{(line.quantity * line.price).toFixed(2)}€</span> | 
                                IVA: <span className="fv-invoice-4">{(line.quantity * line.price * line.vat / 100).toFixed(2)}€</span>
                              </span>
                              <button
                                type="button"
                                onClick={() => removeInvoiceLine(line.id)}
                                className="fv-invoice-43"
                                title="Eliminar línea"
                              >
                                <Trash2 size={12} />
                              </button>
                            </div>
                          </div>

                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Parámetros de cobro e impuestos */}
                  <div className="fv-invoice-44">
                    <div className="fv-invoice-45">
                      <label className="fv-invoice-46">Condiciones de Pago</label>
                      <select 
                        value={invoicePaymentTerms}
                        onChange={(e) => setInvoicePaymentTerms(e.target.value)}
                        className="fv-invoice-47"
                      >
                        <option>Inmediato (Contado)</option>
                        <option>Neto 15 días</option>
                        <option>Neto 30 días</option>
                        <option>Neto 60 días</option>
                      </select>
                    </div>

                    <div className="fv-invoice-45">
                      <label className="fv-invoice-46">Cuenta de Registro Contable</label>
                      <select 
                        value={invoiceBankAcc}
                        onChange={(e) => setInvoiceBankAcc(e.target.value)}
                        className="fv-invoice-47"
                      >
                        <option value="wise-eur">WISE EUR (Plan PGC: 572000 Bancos)</option>
                        <option value="wise-mxn">WISE MXN (Sincronizada)</option>
                        <option value="wise-pln">WISE PLN (Sincronizada)</option>
                      </select>
                    </div>
                  </div>

                  {/* Resumen del importe y desglose */}
                  <div className="fv-invoice-48">
                    <div className="fv-invoice-49">
                      <span>BASE IMPONIBLE (SUBTOTAL):</span>
                      <span className="fv-invoice-18">
                        {invoiceLines.reduce((sum, l) => sum + (l.quantity * l.price), 0).toFixed(2)}€
                      </span>
                    </div>
                    <div className="fv-invoice-49">
                      <span>TOTAL IVA REPERCUTIDO:</span>
                      <span className="fv-invoice-4">
{invoiceLines.reduce((sum, l) => sum + (l.quantity * l.price * l.vat / 100), 0).toFixed(2)}€
                      </span>
                    </div>
                    <div className="fv-invoice-50">
                      <span>TOTAL GENERAL EMISIÓN:</span>
                      <span className="fv-invoice-51">
                        {(
                          invoiceLines.reduce((sum, l) => sum + (l.quantity * l.price), 0) +
                          invoiceLines.reduce((sum, l) => sum + (l.quantity * l.price * l.vat / 100), 0)
                        ).toFixed(2)}€
                      </span>
                    </div>
                    <p className="fv-invoice-52">
                      * AL HACER CLICK EN EMITIR FACTURA SE GENERARÁ EL ASIENTO CONTABLE [DEBE 430000 / HABER 700000 + 477000] AUTOMÁTICAMENTE EN EL DIARIO.
                    </p>
                  </div>

                  {/* Botones */}
                  <div className="fv-invoice-53">
                    <button 
                      type="button"
                      onClick={handleRequestCloseActionModal}
                      className="fv-invoice-54"
                    >
                      Cancelar
                    </button>
                    <button 
                      type="submit"
                      className="fv-invoice-55"
                    >
                      Contabilizar y Emitir Factura
                    </button>
                  </div>

                </form>
              ) : (
                <form onSubmit={(e: any) => {
                  e.preventDefault();
                  const formData = {
                    amount: e.target.amount.value,
                    description: e.target.description.value,
                    entity: e.target.entity ? e.target.entity.value : '',
                    bankAccount: e.target.bankAccount ? e.target.bankAccount.value : '',
                  };
                  handleFormSubmit(activeActionModal.id, formData);
                }} className="finances-form">
                  
                  {/* Monto de transacción */}
                  <div className="finances-form-group">
                    <label className="finances-form-label">Cantidad / Importe (€)</label>
                    <input 
                      name="amount"
                      type="number" 
                      step="0.01" 
                      required 
                      placeholder="0.00"
                      className="finances-form-input"
                    />
                  </div>

                  {/* Descripcion */}
                  <div className="finances-form-group">
                    <label className="finances-form-label">Descripción / Concepto</label>
                    <input 
                      name="description"
                      type="text" 
                      required 
                      placeholder="Ej. Liquidación de honorarios"
                      className="finances-form-input"
                    />
                  </div>

                  {/* Cliente/Proveedor asimilado según categoría */}
                  {activeActionModal.category !== 'other' && (
                    <div className="finances-form-group">
                      <label className="finances-form-label">
                        {activeActionModal.category === 'customers' ? 'Nombre del Cliente' : 'Nombre del Acreedor'}
                      </label>
                      <input 
                        name="entity"
                        type="text" 
                        placeholder={activeActionModal.category === 'customers' ? 'Ej. Librería Central S.L.' : 'Ej. Papelera del Norte'}
                        className="finances-form-input"
                      />
                    </div>
                  )}

                  {/* Selección de Cuenta bancaria para el descargo/abono contable */}
                  <div className="finances-form-group">
                    <label className="finances-form-label">Cuenta Bancaria de Registro</label>
                    <select 
                      name="bankAccount"
                      className="finances-form-select"
                    >
                      <option value="wise-eur">WISE EUR (Banco: 0.19€ / QB: 936.91€)</option>
                      <option value="wise-pln">WISE PLN (Sincronizado: 14.242,00 PLN)</option>
                      <option value="wise-mxn">WISE MXN (Banco: 0.00 MXN / QB: 387.922,79 MXN)</option>
                    </select>
                  </div>

                  {/* Parámetros fiscales o notas adicionales */}
                  <div className="finances-form-row">
                    <div className="finances-form-group">
                      <label className="finances-form-label">Tasa de Impuesto / IVA</label>
                      <select className="finances-form-select">
                        <option>Excempto (0%)</option>
                        <option>IVA Reducido (4%)</option>
                        <option>IVA Editorial (10%)</option>
                        <option>IVA Estándar (21%)</option>
                      </select>
                    </div>
                    <div className="finances-form-group">
                      <label className="finances-form-label">Condiciones de pago</label>
                      <select className="finances-form-select">
                        <option>Inmediato (Contado)</option>
                        <option>Neto 15 días</option>
                        <option>Neto 30 días</option>
                        <option>Neto 60 días</option>
                      </select>
                    </div>
                  </div>

                  {/* Botón de envío */}
                  <div className="finances-form-actions">
                    <button 
                      type="button"
                      onClick={handleRequestCloseActionModal}
                      className="finances-form-btn-cancel"
                    >
                      Cancelar
                    </button>
                    <button 
                      type="submit"
                      className="finances-form-btn-submit"
                    >
                      Registrar Acción
                    </button>
                  </div>

                </form>
              )}
            </div>
          </div>
        )}

        {/* Modal de confirmación para descartar cambios y salir */}
        <Modal
          isOpen={showConfirmCloseModal}
          type="warning"
          title="Descartar cambios"
          message="¿Estás seguro de que deseas salir y descartar todos los cambios introducidos en el formulario?"
          confirmText="Descartar y salir"
          cancelText="Continuar editando"
          onConfirm={handleConfirmDiscardAndClose}
          onCancel={() => setShowConfirmCloseModal(false)}
        />
      </>
    </div>
  );
};
